import type {
  BackendCapabilities,
  BrowserBackend,
  SerializedRequest,
  WorkerRequestMessage,
  WorkerResponseMessage,
} from "./contracts";

export class NativeBrowserBackend implements BrowserBackend {
  private worker: Worker | undefined;
  private ready: Promise<void> | undefined;
  private readonly pending = new Map<
    string,
    { resolve: (message: WorkerResponseMessage) => void; reject: (error: Error) => void }
  >();
  private currentCapabilities: BackendCapabilities = {
    localApi: false,
    virtualFilesystem: false,
    npmRuntime: false,
    virtualHttp: false,
    persistence: false,
    outboundCorsAwareFetch: true,
    crossOriginIsolated: globalThis.crossOriginIsolated === true,
    runtime: "native-browser",
  };

  async start(): Promise<void> {
    if (this.worker) {
      return this.ready;
    }

    this.worker = new Worker(new URL("./backend.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (event: MessageEvent<WorkerResponseMessage>) => this.handleMessage(event.data);
    this.worker.onerror = (event) => {
      const error = new Error(event.message || "Browser-local backend worker failed.");
      for (const pending of this.pending.values()) {
        pending.reject(error);
      }
      this.pending.clear();
    };

    this.ready = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("Browser-local backend start timed out.")), 10_000);
      const originalCapabilities = this.currentCapabilities;
      const waitForReady = (event: MessageEvent<WorkerResponseMessage>) => {
        if (event.data.type !== "ready") {
          return;
        }
        window.clearTimeout(timeout);
        this.currentCapabilities = event.data.capabilities;
        this.worker?.removeEventListener("message", waitForReady);
        resolve();
      };
      this.worker?.addEventListener("message", waitForReady);
      if (originalCapabilities.localApi) {
        window.clearTimeout(timeout);
        resolve();
      }
    });

    this.worker.postMessage({ type: "request", id: crypto.randomUUID(), request: {
      url: `${window.location.origin}/api/health`,
      method: "GET",
      headers: {},
      body: null,
    }} satisfies WorkerRequestMessage);
    await this.ready;
  }

  async request(request: Request): Promise<Response> {
    await this.start();
    const id = crypto.randomUUID();
    const message: WorkerRequestMessage = {
      type: "request",
      id,
      request: await serializeRequest(request),
    };
    const response = await this.send(id, message);
    if (response.type !== "response") {
      throw new Error("The browser-local backend returned an invalid response.");
    }
    return new Response(response.response.body, {
      status: response.response.status,
      headers: response.response.headers,
    });
  }

  capabilities(): BackendCapabilities {
    return this.currentCapabilities;
  }

  async snapshot(): Promise<Uint8Array> {
    await this.start();
    const id = crypto.randomUUID();
    const response = await this.send(id, { type: "snapshot", id });
    if (response.type !== "snapshot") {
      throw new Error("The browser-local backend returned an invalid snapshot response.");
    }
    return new Uint8Array(response.snapshot);
  }

  async restore(snapshot: Uint8Array): Promise<void> {
    await this.start();
    const id = crypto.randomUUID();
    const response = await this.send(id, {
      type: "restore",
      id,
      snapshot: toArrayBuffer(snapshot),
    });
    if (response.type !== "restored") {
      throw new Error("The browser-local backend did not restore the snapshot.");
    }
  }

  async stop(): Promise<void> {
    if (!this.worker) {
      return;
    }
    const id = crypto.randomUUID();
    try {
      await this.send(id, { type: "stop", id });
    } finally {
      this.worker.terminate();
      this.worker = undefined;
      this.ready = undefined;
      this.currentCapabilities = {
        ...this.currentCapabilities,
        localApi: false,
        persistence: false,
      };
    }
  }

  private send(id: string, message: WorkerRequestMessage): Promise<WorkerResponseMessage> {
    return new Promise<WorkerResponseMessage>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker?.postMessage(message, message.type === "restore" ? [message.snapshot] : []);
    });
  }

  private handleMessage(message: WorkerResponseMessage): void {
    if (message.type === "ready") {
      this.currentCapabilities = message.capabilities;
      return;
    }
    if (message.type === "changed") {
      window.dispatchEvent(new CustomEvent("browser-local-backend-changed", { detail: message }));
      return;
    }
    if ("id" in message) {
      const pending = this.pending.get(message.id);
      if (!pending) {
        return;
      }
      this.pending.delete(message.id);
      if (message.type === "error") {
        pending.reject(new Error(`${message.code}: ${message.message}`));
      } else {
        pending.resolve(message);
      }
    }
  }
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

async function serializeRequest(request: Request): Promise<SerializedRequest> {
  return {
    url: request.url,
    method: request.method,
    headers: Object.fromEntries(request.headers.entries()),
    body: request.method === "GET" || request.method === "HEAD" ? null : await request.clone().text(),
  };
}
